/* THE CANVA TAB in "Choose image" (step C1, docs/canva-and-own-words-options.md).
 *
 * Her designs, newest first; pick one (and a page, when it has several) and it
 * comes back as a picture in her GHL library — Canva's own download links die
 * after a day, so a link straight from Canva would leave a broken picture
 * tomorrow. Admin-only for now (her call, 2026-10-01); MediaPickButton only
 * offers this tab where it offers GHL at all.
 *
 * Connecting opens Canva's Allow screen in a small WINDOW, so she never leaves
 * the slide or post she is on (utils/canva.js finishes the sign-in there).
 */
import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { CANVA_MESSAGE, callCanva } from "../../utils/canva";
import { B, btnGhost, input } from "./tokens";

export function CanvaPane({ onPick, onClose }) {
  const { session } = useSession();
  const [status, setStatus] = useState({ loading: true });
  const [list, setList] = useState({ designs: [], continuation: null, loading: false, error: "" });
  const [q, setQ] = useState("");
  const [paging, setPaging] = useState(null);      // a multi-page design waiting for its page
  const [busy, setBusy] = useState("");            // the design id being brought in
  const [err, setErr] = useState("");

  const load = useCallback(async ({ query = "", more = null } = {}) => {
    setList((l) => ({ ...l, loading: true, error: "" }));
    const r = await callCanva(session, { job: "designs", query, continuation: more || "" });
    if (!r.ok) {
      if (r.connected === false) setStatus({ connected: false });
      setList((l) => ({ ...l, loading: false, error: r.connected === false ? "" : (r.error || "Canva didn't answer.") }));
      return;
    }
    setStatus({ connected: true, name: r.name || "" });
    setList((l) => ({ designs: more ? [...l.designs, ...r.designs] : r.designs, continuation: r.continuation, loading: false, error: "" }));
  }, [session]);

  useEffect(() => {
    let live = true;
    (async () => {
      /* A sign-in finished in THIS window (the pop-up was blocked) left its
         result behind; say it once. */
      try {
        const left = JSON.parse(window.sessionStorage.getItem("brill.canva.result") || "null");
        if (left) { window.sessionStorage.removeItem("brill.canva.result"); if (!left.ok) setErr(left.error); }
      } catch { /* private window */ }
      const r = await callCanva(session, { job: "status" });
      if (!live) return;
      if (!r.ok) { setStatus({ connected: false, setup: !!r.setup }); setErr(r.error || ""); return; }
      setStatus({ connected: !!r.connected, name: r.name || "", expired: !!r.expired });
      if (r.connected) load();
    })();
    return () => { live = false; };
  }, [session, load]);

  /* The Connect window reports back here. Only from Brill's own origin. */
  useEffect(() => {
    const hear = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== CANVA_MESSAGE) return;
      if (e.data.ok) { setErr(""); setStatus({ connected: true, name: e.data.name || "" }); load(); }
      else setErr(e.data.error || "Canva didn't connect.");
    };
    window.addEventListener("message", hear);
    return () => window.removeEventListener("message", hear);
  }, [load]);

  const connect = async () => {
    setErr("");
    /* Opened NOW, inside her click, or the browser blocks it as a pop-up;
       pointed at Canva once Brill has the address. */
    const w = window.open("", "brill-canva", "width=560,height=760");
    const r = await callCanva(session, { job: "start", origin: window.location.origin });
    if (!r.ok) { if (w) w.close(); setErr(r.error || "Brill couldn't start the Canva sign-in."); return; }
    if (w) w.location.href = r.url;
    else window.location.assign(r.url);   // pop-ups blocked: go in this window and come back
  };

  const disconnect = async () => {
    await callCanva(session, { job: "disconnect" });
    setStatus({ connected: false });
    setList({ designs: [], continuation: null, loading: false, error: "" });
  };

  const bring = async (d, page) => {
    setPaging(null);
    setErr("");
    setBusy(d.id);
    const r = await callCanva(session, { job: "pick", designId: d.id, title: d.title, page, pages: d.pages });
    setBusy("");
    if (!r.ok) { setErr(r.error || "That design didn't come through."); return; }
    onPick(r.picture.url);
    onClose();
  };

  if (status.loading) return <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 2px" }}>Looking…</div>;

  if (!status.connected) {
    return (
      <div style={{ fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>
        {status.setup ? null : (
          <>
            <div style={{ marginBottom: 10 }}>
              {status.expired ? "Canva signed Brill out. Connect again to see your designs." : "Connect your Canva to bring a design in as a picture. It saves into your GHL library, in a Canva folder."}
            </div>
            <button type="button" onClick={connect}
              style={{ ...btnGhost, padding: "7px 13px", fontSize: 12.5, color: B.teal, borderColor: B.tealLine }}>
              Connect Canva
            </button>
          </>
        )}
        {err && <div style={{ color: B.amber, marginTop: 10 }}>{err}</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your designs"
          onKeyDown={(e) => { if (e.key === "Enter") load({ query: q }); }}
          style={{ ...input, padding: "7px 11px", fontSize: 12.5 }} />
        <button type="button" onClick={() => load({ query: q })} style={{ ...btnGhost, padding: "6px 10px", fontSize: 12 }}>Search</button>
      </div>
      {err && <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6, marginBottom: 10 }}>{err}</div>}
      {list.error && <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6, marginBottom: 10 }}>{list.error}</div>}

      {paging && (
        <div style={{ border: `1px solid ${B.tealLine}`, background: B.tealQuiet, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, color: B.text, marginBottom: 8 }}>
            <b>{paging.title}</b> has {paging.pages} pages. Which one?
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {Array.from({ length: Math.min(paging.pages, 40) }, (_, i) => (
              <button key={i} type="button" onClick={() => bring(paging, i + 1)}
                style={{ ...btnGhost, padding: "4px 10px", fontSize: 12, minWidth: 34 }}>{i + 1}</button>
            ))}
            <button type="button" onClick={() => setPaging(null)} style={{ background: "none", border: "none", color: B.dim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          </div>
        </div>
      )}

      {busy && <div style={{ fontSize: 12.5, color: B.teal, marginBottom: 10 }}>Bringing it in from Canva — this takes a few seconds…</div>}

      {!list.loading && !list.designs.length && !list.error && (
        <div style={{ fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>{q.trim() ? "No designs match that." : "No designs in your Canva yet."}</div>
      )}

      {list.designs.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 8, maxHeight: 320, overflowY: "auto" }}>
          {list.designs.map((d) => (
            <button key={d.id} type="button" title={d.title} disabled={!!busy}
              onClick={() => (d.pages > 1 ? setPaging(d) : bring(d, 1))}
              style={{ border: `1px solid ${busy === d.id ? B.teal : B.border}`, borderRadius: 8, padding: 0, background: B.surface2,
                cursor: busy ? "wait" : "pointer", overflow: "hidden", display: "block", opacity: busy && busy !== d.id ? 0.5 : 1 }}>
              {d.thumb
                ? <img src={d.thumb} alt="" style={{ display: "block", width: "100%", height: 66, objectFit: "contain", background: "var(--bg-quiet)" }} />
                : <span style={{ display: "block", height: 66, background: "var(--bg-quiet)" }} />}
              <span style={{ display: "block", fontSize: 10.5, color: B.dim, padding: "4px 5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.pages > 1 ? `${d.title} · ${d.pages}p` : d.title}
              </span>
            </button>
          ))}
        </div>
      )}
      {list.loading && <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 2px" }}>Looking…</div>}
      {list.continuation && !list.loading && (
        <button type="button" onClick={() => load({ query: q, more: list.continuation })}
          style={{ ...btnGhost, padding: "6px 10px", fontSize: 12, marginTop: 10 }}>More designs</button>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 11.5, color: B.faint }}>
        <span>{status.name ? `Connected as ${status.name}` : "Canva connected"}</span>
        <button type="button" onClick={disconnect}
          style={{ background: "none", border: "none", color: B.dim, fontSize: 11.5, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>Disconnect</button>
      </div>
    </div>
  );
}
